
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { processReferral } from "@/services/referralService";
import { getAffiliateIdFromCookie } from "@/hooks/referrals/referralUtils";
import { setAffiliatedByValue } from "@/utils/affiliationUtils";
import { createCampaignTrial } from "@/utils/campaignUtils";
import { subscribeToKlaviyo } from "@/utils/authUtils";

/**
 * This hook handles processing of referral codes and affiliate IDs
 * for users who sign up with OAuth providers (Google, Facebook, etc.)
 * It mirrors the functionality in the email signup process
 */
export const useOAuthSignUpProcess = () => {
  // Process OAuth signup events
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // We only process for specific OAuth-related signup events
      const isOAuthSignUp = event === 'SIGNED_IN' && 
                           session?.user?.app_metadata?.provider && 
                           session.user.app_metadata.provider !== 'email';

      if (isOAuthSignUp) {
        try {
          console.log("OAuth signup detected, processing additional signup steps");
          const userId = session.user.id;
          
          // Log current state of affiliate tracking
          const affiliateId = getAffiliateIdFromCookie();
          console.log("Current affiliate ID from cookie:", affiliateId);
          
          // Wait a moment for Supabase auth hooks to fully complete
          // This helps ensure the user record exists before further processing
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // If we have an affiliate ID, directly set it
          if (affiliateId) {
            const directResult = await setAffiliatedByValue(userId, affiliateId);
            console.log("Direct setting of affiliated_by result:", directResult);
          }
          
          // Parallel execution of post-signup tasks, exactly like email signup process
          const results = await Promise.allSettled([
            // Process any referral relationships
            processReferral(userId),
            // Create campaign trial if applicable
            //createCampaignTrial(userId),
            // Subscribe the user to Klaviyo
            subscribeToKlaviyo(userId, 
              session.user.email || '', 
              extractFirstName(session.user), 
              extractLastName(session.user))
          ]);
          
          // Log results for troubleshooting
          const taskNames = ['Process Referral', 'Create Campaign Trial', 'Klaviyo Subscription'];
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
              console.log(`Task "${taskNames[index]}" completed successfully:`, result.value);
            } else {
              console.error(`Task "${taskNames[index]}" failed:`, result.reason);
            }
          });
        } catch (error) {
          console.error("Error processing OAuth signup:", error);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
};

// Helper function to extract first name from OAuth user data
function extractFirstName(user: any): string {
  return user.user_metadata?.first_name || 
         user.user_metadata?.name?.split(' ')[0] || 
         user.user_metadata?.given_name || 
         '';
}

// Helper function to extract last name from OAuth user data
function extractLastName(user: any): string {
  return user.user_metadata?.last_name || 
         (user.user_metadata?.name?.split(' ').slice(1).join(' ') || '') ||
         user.user_metadata?.family_name || 
         '';
}
